import {
    CAR_NAMES,
    JumpingWildAnimStates,
    JumpinWildSkins,
    RoadKillSymbolStates,
    SYMBOL_NAME
} from "../component/enums/RoadKillEnums"
import {RoadKill} from "../RoadKill";
import {gsap, TimelineLite} from "gsap";
import {RoadKillGameAssets} from "../RoadKillGameAssets";
import {Multiplier} from "../component/Multiplier";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {RKHelper} from "../utils/RKHelper";
import {BLEND_MODES} from 'pixi.js';
import {JumpingWild} from "../component/JumpingWild";
import {ISpecialSymData} from "../server/RoadKillData";

export class RoadKillSymbol extends PIXI.Container {
    public state: RoadKillSymbolStates;
    protected _splitSpine: PIXI.spine.Spine;
    protected _newStateSprite: PIXI.Sprite;
    protected _currentStateSprite: PIXI.Sprite;
    protected _darkSquareGfx: PIXI.Graphics;
    private gfxTl: gsap.core.Timeline;
    private _wildOverlay: PIXI.spine.Spine;
    private _glowTexture: PIXI.Sprite;
    private _splitX2Sprite: PIXI.Sprite;

    constructor(symbolName: SYMBOL_NAME, reelId: number) {
        super();
        this.reelId = reelId;
        this.isRemoved = false;
        this.createDarkGraphics();
        this.create(symbolName);
    }

    private _animation: PIXI.spine.Spine;

    get animation(): PIXI.spine.Spine {
        return this._animation;
    }

    private _trappedCar: PIXI.Sprite;

    get trappedCar(): PIXI.Sprite {
        return this._trappedCar;
    }

    private _multiplier: Multiplier;

    get multiplier(): Multiplier {
        return this._multiplier;
    }

    private _reelId: number;

    public get reelId(): number {
        return this._reelId;
    }

    public set reelId(value: number) {
        this._reelId = value;
    }

    private _isRemoved: boolean;

    public set isRemoved(value: boolean) {
        this._isRemoved = value;
    }

    private _symbolName: SYMBOL_NAME;

    get symbolName(): SYMBOL_NAME {
        return this._symbolName;
    }

    set symbolName(symbolName: SYMBOL_NAME) {
        this._symbolName = symbolName;
    }

    private _split: boolean = false;

    get split(): boolean {
        return this._split;
    }

    public glowPreHit(hits: boolean, duration: number = 0.25, winLvl: number = 0): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (hits) {
            this.addChildrenRespectively([this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx])
            if (RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName)) {
                tl.add(() => {
                    this._animation.alpha = 1;
                    this._animation.autoUpdate = true;
                    this._animation.state.setAnimation(0, "tiny_win", false).timeScale = this._animation.skeleton.data.findAnimation("tiny_win").duration / duration;
                    this._newStateSprite.visible = false;
                    this._currentStateSprite.visible = false
                }, 0);
                tl.to(this, {duration: duration}, 0);
            } else {
                tl.add(this.Glow(duration, winLvl), 0);
            }
        }
        return tl;
    }

    public setSpineAlpha(alpha: number = 0, dur: number = RoadKillGameConfig.HIDE_AND_ALPHA_DURATION): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (!(RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName))) {
            if (this._animation) {
                tl.to(this._animation, {alpha: alpha, duration: dur}, 0);
            }
            if (this._splitSpine) {
                tl.to(this._splitSpine, {alpha: alpha, duration: dur}, 0);
            }
        }
        return tl;
    }

    public setNonAlpha(alpha: number = 0.55, setVisibility: boolean = false): void {
        this._darkSquareGfx.alpha = alpha;
        if (setVisibility) {
            this._darkSquareGfx.visible = true;
        }
    }

    public markNonWin(enable: boolean, alpha: number = 0.55, durr: number = RoadKillGameConfig.HIDE_AND_ALPHA_DURATION): void {
        if (RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName)) {
            const tl: TimelineLite = new TimelineLite();
            this._animation.autoUpdate = true;
            if (enable) {
                tl.add(() => {
                    this._darkSquareGfx.visible = false;
                    this._animation.alpha = 1;
                    this._animation.state.setAnimation(0, "no_win", false).timeScale = (durr / this._animation.skeleton.data.findAnimation("no_win").duration);
                });
                tl.to(this, {duration: durr});
            } else {
                tl.add(RKHelper.playSpineWithTimeline(this._animation, 0, "idle")).progress(1);
            }
            return;
        }
        if (enable && this._darkSquareGfx) {
            if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(this._symbolName) > -1) {
                this._darkSquareGfx.scale.set(1.1, 1.06);
            } else {
                this._darkSquareGfx.scale.set(1);
            }
            this.addChild(this._darkSquareGfx);
        }
        this.gfxTl = RoadKillUtils.setTimeline(this.gfxTl, new gsap.core.Timeline({
            onComplete: () => {
                this._darkSquareGfx.alpha = enable ? alpha : 0
            }
        }), true);
        this.gfxTl.to(this._darkSquareGfx, {duration: durr, alpha: enable ? alpha : 0});
        this.gfxTl.add(() => {
            this.addChildrenRespectively([this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx]);
        })
    }

    public createTrappedCar(carName: CAR_NAMES): void {
        this._trappedCar = new PIXI.Sprite(RoadKill.assetLoader.getTextures(carName === CAR_NAMES.GREEN ? "greenTrapCar" : "redTrapCar")[0]);
        this._trappedCar.anchor.set(0.5, 1);
        this._trappedCar.position.y += 9;
        this._trappedCar.position.x - 1;
        this._trappedCar.alpha = 0;
        this._trappedCar.name = `${carName}Car`
        this.addChild(this._trappedCar);
    }

    public changeSymbol(toSymbol: SYMBOL_NAME): void {
        // const tl: TimelineLite = new TimelineLite();
        //this.state = RoadKillSymbolStates.NO_WIN;
        this.removeChildren();
        this.create(toSymbol);
        // this.symbolName = toSymbol;
        // this.name = toSymbol.toUpperCase();
        // this.init(RoadKillSymbolStates.NORMAL);
        //this.setState(RoadKillSymbolStates.NORMAL, "", 0, true).timeScale(10);
        this.setNewStateSpriteTexture("");
        this._currentStateSprite.texture = this._newStateSprite.texture;
        this._currentStateSprite.alpha = 1;
        this._newStateSprite.alpha = 0;
        if (RoadKill.model.gameData && RoadKill.model.gameData.specialSymbolList) {
            RoadKill.model.gameData.specialSymbolList.forEach((animalData) => {
                if (toSymbol === animalData.symName && animalData.upgraded) {
                    this.setNewStateSpriteTextureForSWSym(`${toSymbol}Golden`);
                }
            })
        }
        // return tl;
    }

    public setState(state: RoadKillSymbolStates, textureName: string, changeSpeed: number = 0, forceChange: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this.state === state && !forceChange) {
            return tl;
        }

        if (this.symbolName === SYMBOL_NAME.SCATTER && state === RoadKillSymbolStates.SPIN) {
            state = RoadKillSymbolStates.NORMAL;
            textureName = "";
        }

        if (state !== RoadKillSymbolStates.LAND) {
            this.state = state;
        }
        if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(this.symbolName) > -1 && textureName === "spin") {
            textureName = ""; //in case of SW1 SW2 SW3 we will not blur in spin.
            tl.add(() => { //216
                this.markNonWin(true);
            });
        }
        if (state === RoadKillSymbolStates.WIN || state === RoadKillSymbolStates.LAND) {
            if (this._splitSpine) {
                this._splitSpine.autoUpdate = true;
            } else {
                if (!this._animation) {
                    this._animation = this.getSpine();
                }
            }

            const playingSpine: PIXI.spine.Spine = this._splitSpine ? this._splitSpine : this._animation,
                childrenArray = [this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx];

            let animationName: string = this.getSymbolAnimName(state, this.symbolName);
            if (animationName) {
                tl.add(() => {
                    if (RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName)) {
                        RKHelper.playSpineWithTimeline(playingSpine, 0, animationName)
                    } else {
                        playingSpine.state.setAnimation(0, animationName, false);
                    }
                });

                if (RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName)) {
                    const JW: JumpingWild = this.getJumpingWild(this.symbolName);
                    tl.add(() => {
                        JW.state.setAnimation(0, JumpingWildAnimStates.WIN_STATE, true);
                        JW.animationState = JumpingWildAnimStates.WIN_STATE;
                    }, tl.duration() + JW.skeleton.data.findAnimation(JumpingWildAnimStates.WIN).duration)
                }
                tl.to(this._animation, 0.1, {alpha: 1});
            }
            this.addChildrenRespectively(childrenArray);
        } else if (state === RoadKillSymbolStates.NO_WIN) {
            tl.add(() => {
                this.markNonWin(true);
            });
        } else {
            tl.add(() => this.setNewStateSpriteTexture(textureName));
            tl.add([
                gsap.to(this._currentStateSprite, {
                    duration: changeSpeed,
                    alpha: 0,
                    ease: "power2.in"
                }),
                gsap.to(this._newStateSprite, {
                    duration: changeSpeed,
                    alpha: 1,
                    ease: "power2.out"
                })
            ]);
            tl.add([
                () => {
                    this._currentStateSprite.texture = this._newStateSprite.texture;
                    this._currentStateSprite.alpha = 1;
                    this._newStateSprite.alpha = 0
                },
            ]);
        }

        return tl;
    }

    public setNewStateSpriteTexture(textureName?: string) {
        if (textureName == "none") {
            this._newStateSprite.texture = PIXI.Texture.EMPTY;
        } else {
            this._newStateSprite.texture = RoadKill.assetLoader.getTextures(RoadKillGameAssets.getSymbolStateTexturePath(this._symbolName, textureName))[0];
        }
    }

    public getSymbolAnimName(state: RoadKillSymbolStates, symbolName: SYMBOL_NAME): string {
        let animationName: string = "",
            isJW: boolean = RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName);
        switch (state) {
            case RoadKillSymbolStates.WIN:
                animationName = isJW ? "win" : symbolName + "_win";
                break;
            case RoadKillSymbolStates.NO_WIN:
                //animationName = symbolName + "_spinEnd";
                animationName = isJW ? "idle" : symbolName + "_idle";
                break;
            case RoadKillSymbolStates.NORMAL:
                animationName = isJW ? "idle" : symbolName + "_idle";
                break;
            case RoadKillSymbolStates.LAND:
                animationName = "land";
                break;
        }
        return animationName;
    }

    public init(initState: RoadKillSymbolStates): RoadKillSymbol {
        this.getStateAnimation(initState);
        return this;
    }

    public getStateAnimation(state: RoadKillSymbolStates, changeSpeed: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this.state == state) {
            return tl;
        }

        switch (state) {
            case RoadKillSymbolStates.NORMAL:
                tl.add(this.setState(state, ""));
                break;
            case RoadKillSymbolStates.SPIN:
                tl.add(this.setState(state, "spin"));
                break;
            case RoadKillSymbolStates.NO_WIN:
                tl.add(gsap.to(this, {
                    tint: 0.3,
                    pixi: {alpha: 1, saturation: 0, combineCMF: false, brightness: 0.5}
                }));
                break;
            default:

        }

        if (tl.duration() == 0) {
            tl.add(gsap.to(this, {duration: 0.0001}));
        }

        return tl;
    }

    public addMultiplier(value: number): void {
        const JW: JumpingWild[] = RoadKill.jumpingWildView.getWilds();
        if ((this.symbolName === SYMBOL_NAME.SW1 && JW[0].splitStateVisible) || (this.symbolName === SYMBOL_NAME.SW3 && JW[2].splitStateVisible)) {
            if (!this._splitX2Sprite) {
                this._splitX2Sprite = this.createX2Sprite();
            }
            new TimelineLite().to(this._splitX2Sprite, {alpha: 1, duration: 0.1});
            this.addChildrenRespectively([this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx]);
        }
        // if (!this._multiplier) {
        //     this._multiplier = new Multiplier(RoadKillGameAssets.BITMAP_FONT_NUMBERS_MULTIPLIER, 22, "x");
        //     this._multiplier.position.y = -73;
        //     this.addChildAt(this._multiplier, 2);
        // }
        // this._multiplier.value = value;
    }

    public createWildOverlay(reelId: number, symId: number, visible: boolean = false): void {
        if (this._wildOverlay) {
            return;
        }
        this._wildOverlay = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("wildOverlay"));
        this._wildOverlay.name = `${reelId}${symId}`;
        this._wildOverlay.position.set(0, -126);
        this._wildOverlay.visible = visible;
        this.addChildAt(this._wildOverlay, 3);
        this.addChildrenRespectively([this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx]);
    }

    public playWildOverlayLandAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (!this._wildOverlay.visible) {
            tl.add(() => {
                this._wildOverlay.visible = true;
            });
        }
        tl.add(RKHelper.playSpineWithTimeline(this._wildOverlay, 0, "W_In")).timeScale(20);
        return tl;

    }

    public playWildOverlayWinAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (!this._wildOverlay.visible) {
            tl.add(() => {
                this._wildOverlay.visible = true;
            });
        }
        tl.add(() => {
            this.addChildrenRespectively([this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx]);
        });
        tl.add(RKHelper.playSpineWithTimeline(this._wildOverlay, 0, "W_Win"));
        return tl;

    }

    public hasWild(): boolean {
        return !!this._wildOverlay;
    }

    public addToggleWild(): void {
        if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(this.symbolName) > -1 && !this._wildOverlay) {
            this._wildOverlay = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("wildOverlay"));
            this._wildOverlay.name = `${this.reelId}${0}`;
            this._wildOverlay.position.set(0, -70);
            this._wildOverlay.visible = true;
            this._wildOverlay.alpha = 1;
            this.addChildAt(this._wildOverlay, 2);
        }
    }

    public setSplit(value: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._split != value) {
            this._split = value;
            if (this._split) {
                if (RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName)) {
                    if (!this._splitX2Sprite) {
                        this._splitX2Sprite = this.createX2Sprite();
                        tl.to(this._splitX2Sprite, {alpha: 1, duration: 0.1});
                    }
                } else {
                    if (!this._splitSpine) {
                        this._splitSpine = this.createSplitSpine();
                    }
                    this.addChildrenRespectively([this._animation, this._splitSpine, this._splitX2Sprite, this.trappedCar, this._wildOverlay, this._glowTexture, this._darkSquareGfx]);
                    tl.to(this._splitSpine, {alpha: 1, duration: 0.001}, 0);
                    tl.add(RKHelper.playSpineWithTimeline(this._splitSpine, 0, this.getSymbolAnimName(RoadKillSymbolStates.NORMAL, this.symbolName)));
                }
            }
        }
        return tl;
    }

    private createX2Sprite(): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(RoadKill.assetLoader.getTextures("x2")[0]);
        sprite.position.set(37, -124);
        sprite.scale.set(0.8);
        sprite.anchor.set(0.5);
        sprite.alpha = 0;
        sprite.visible = true;
        sprite.name = "x2Split";
        this.addChild(sprite);
        return sprite;
    }

    private addChildrenRespectively(elementArray: any[]): void {
        this.addChild(this._newStateSprite, this._currentStateSprite,);
        for (let idx: number = 0; idx < elementArray.length; idx++) {
            if (elementArray[idx]) {
                this.addChild(elementArray[idx]);
            }
        }
    }

    private createSplitSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset(this.symbolName + "_split"));
        spine.position.set(-2, -76);
        spine.alpha = 0;
        spine.visible = true;
        spine.name = "slpit_spine";
        return spine;
    }

    private getJumpingWild(symName: string): JumpingWild {
        const JWArray: JumpingWild[] = RoadKill.jumpingWildView.getWilds();
        if (symName === SYMBOL_NAME.SW1) {
            return JWArray[0];
        } else if (symName === SYMBOL_NAME.SW2) {
            return JWArray[1];
        } else {
            return JWArray[2];
        }
    }

    private Glow(durr: number = 0.25, lvl: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (!this._glowTexture) {
            this._glowTexture = this.addChild(new PIXI.Sprite())
            this._glowTexture.anchor.set(0.5, 1);
            this._glowTexture.position.y = 8;
            this._glowTexture.blendMode = BLEND_MODES.ADD;
        }

        this._glowTexture.texture = (RoadKill.assetLoader.getTextures("glow")[0]);

        this._glowTexture.visible = true;
        tl.fromTo(this._glowTexture, {alpha: 0.8}, {alpha: 0, duration: durr}, 0);

        return tl;
    }

    private setNewStateSpriteTextureForSWSym(textureName: string) {
        this._currentStateSprite.texture = RoadKill.assetLoader.getTextures(textureName)[0];
    }

    private create(symbolName: SYMBOL_NAME): void {
        this._symbolName = symbolName;

        this._currentStateSprite = new PIXI.Sprite();
        this._currentStateSprite.name = "currentStateSprite";
        this._currentStateSprite.position.set(0, -72);
        this._currentStateSprite.anchor.set(0.5, 0.5);
        this._newStateSprite = new PIXI.Sprite();
        this._newStateSprite.name = "newStateSprite";
        this._newStateSprite.position.set(0, -72);
        this._newStateSprite.anchor.set(0.5, 0.5);
        this._newStateSprite.alpha = 0;


        this.addChild(this._newStateSprite);
        this.addChild(this._currentStateSprite);
        if (RoadKillGameConfig.WILD_SYMBOLS.includes(this.symbolName)) {
            this._animation = this.getSpine();
            this._animation.position.set(-2, -76);
            this._animation.alpha = 0;
            this._animation.visible = true;
            this._animation.name = "normal_win_spine";
            this.addChild(this._animation);
        }
        this.addChild(this._darkSquareGfx);

        this.symbolName = symbolName;
        this.name = symbolName.toUpperCase();

    }

    private createDarkGraphics(): void {
        this._darkSquareGfx = new PIXI.Graphics();
        this._darkSquareGfx.alpha = 0;
        this._darkSquareGfx.zIndex = 300;
        this._darkSquareGfx.clear();
        this._darkSquareGfx.beginFill(0x000000, 1);
        this._darkSquareGfx.drawRect(this.position.x, this.position.y + 8, RoadKillGameConfig.SYMBOL_WIDTH, RoadKillGameConfig.SYMBOL_HEIGHT);
        this._darkSquareGfx.endFill();
        this._darkSquareGfx.pivot.set(RoadKillGameConfig.SYMBOL_WIDTH / 2, RoadKillGameConfig.SYMBOL_HEIGHT);

    }

    private getSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset(this.symbolName)),
            animalData: ISpecialSymData[] = RoadKill.model.gameData?.specialSymbolList || RoadKill.model.initData?.specialSymbolList;
        for (let idx: number = 0; idx < animalData.length; idx++) {
            if (animalData[idx].symName === this.symbolName) {
                if (animalData[idx].upgraded) {
                    spine.skeleton.setSkinByName(JumpinWildSkins.UPGRADED);
                } else {
                    spine.skeleton.setSkinByName(JumpinWildSkins.NORMAL);
                }
            }
        }
        spine.position.set(-2, -76);
        spine.alpha = 0;
        spine.visible = true;
        return spine;
    }
}